import { type CurrencyCode, minorUnitOf } from "./currency.ts";
import { DEFAULT_ROUNDING, divideRounded, pow10, type RoundingMode } from "./rounding.ts";

/**
 * An amount of money as an integer count of minor units, tagged with its
 * currency. Never a float: `02-architecture.md` § Money rule 1.
 *
 * The currency tag is not decoration — it is what stops a KHR figure being
 * added to a USD one on a dual-currency bill.
 */
export type Money = {
  readonly amount: bigint;
  readonly currency: CurrencyCode;
};

export function money(amount: bigint, currency: CurrencyCode): Money {
  return { amount, currency };
}

export function zero(currency: CurrencyCode): Money {
  return { amount: 0n, currency };
}

export function isZero(m: Money): boolean {
  return m.amount === 0n;
}

export function isNegative(m: Money): boolean {
  return m.amount < 0n;
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new TypeError(`Currency mismatch: ${a.currency} and ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount + b.amount, currency: a.currency };
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return { amount: a.amount - b.amount, currency: a.currency };
}

export function negate(m: Money): Money {
  return { amount: -m.amount, currency: m.currency };
}

export function absolute(m: Money): Money {
  return { amount: m.amount < 0n ? -m.amount : m.amount, currency: m.currency };
}

export function sum(items: readonly Money[], currency: CurrencyCode): Money {
  let total = 0n;
  for (const item of items) {
    if (item.currency !== currency) {
      throw new TypeError(`Currency mismatch: ${item.currency} in a ${currency} sum`);
    }
    total += item.amount;
  }
  return { amount: total, currency };
}

/** -1, 0 or 1. Throws on a currency mismatch rather than ordering nonsense. */
export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  if (a.amount < b.amount) return -1;
  if (a.amount > b.amount) return 1;
  return 0;
}

export function equals(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amount === b.amount;
}

export function lessThan(a: Money, b: Money): boolean {
  return compare(a, b) === -1;
}

export function greaterThan(a: Money, b: Money): boolean {
  return compare(a, b) === 1;
}

export function min(a: Money, b: Money): Money {
  return compare(a, b) <= 0 ? a : b;
}

export function max(a: Money, b: Money): Money {
  return compare(a, b) >= 0 ? a : b;
}

/** Whole-number multiplication. Exact — no rounding is possible or needed. */
export function multiply(m: Money, factor: bigint): Money {
  return { amount: m.amount * factor, currency: m.currency };
}

/**
 * A quantity that may be fractional — weighed items sell 1.250 kg. Held as a
 * scaled integer so line arithmetic never touches a float.
 */
export type Quantity = {
  readonly value: bigint;
  readonly scale: number;
};

export function quantity(value: bigint, scale = 0): Quantity {
  if (!Number.isInteger(scale) || scale < 0) {
    throw new RangeError(`quantity: scale must be a non-negative integer, got ${scale}`);
  }
  return { value, scale };
}

/** Parses "1.250" into `{ value: 1250n, scale: 3 }`. */
export function parseQuantity(decimal: string): Quantity {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(decimal.trim());
  if (!match) throw new SyntaxError(`parseQuantity: not a decimal number: ${decimal}`);
  const [, sign, whole, fraction = ""] = match;
  const digits = `${whole}${fraction}`;
  const value = BigInt(digits) * (sign === "-" ? -1n : 1n);
  return { value, scale: fraction.length };
}

/**
 * Price × quantity. Rounds once, to the currency's minor unit, because a
 * fractional quantity produces a fractional amount and the line is where the
 * spec says rounding happens.
 */
export function multiplyByQuantity(
  m: Money,
  qty: Quantity,
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money {
  if (qty.scale === 0) return multiply(m, qty.value);
  return {
    amount: divideRounded(m.amount * qty.value, pow10(qty.scale), mode),
    currency: m.currency,
  };
}

/**
 * Splits `m` into shares proportional to `weights`, distributing the rounding
 * remainder by largest fractional part so the shares sum back to `m` exactly.
 *
 * This is how a whole-bill discount is prorated across lines
 * (`03-data-model.md` § 5.2 `bill_share`) and how a subscription addon is
 * prorated against the parent term. A naive per-share round loses or invents a
 * minor unit, which shows up as a bill that does not add up.
 */
export function allocate(m: Money, weights: readonly bigint[]): Money[] {
  if (weights.length === 0) throw new RangeError("allocate: no weights given");
  if (weights.some((w) => w < 0n)) throw new RangeError("allocate: weights must not be negative");

  const totalWeight = weights.reduce((acc, w) => acc + w, 0n);
  if (totalWeight === 0n) {
    // Nothing to weight by — put the whole amount on the first share so the
    // invariant "shares sum to the original" still holds.
    return weights.map((_, i) =>
      i === 0 ? { amount: m.amount, currency: m.currency } : zero(m.currency),
    );
  }

  const negative = m.amount < 0n;
  const total = negative ? -m.amount : m.amount;

  const shares = weights.map((w) => (total * w) / totalWeight);
  const remainders = weights.map((w) => (total * w) % totalWeight);
  const distributed = shares.reduce((acc, s) => acc + s, 0n);

  // Hand the leftover minor units out to the largest remainders first, ties
  // going to the earlier index so the result is deterministic. The leftover is
  // always smaller than the number of shares, so one pass suffices.
  const order = remainders
    .map((remainder, index) => ({ remainder, index }))
    .sort((a, b) =>
      a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1,
    );

  const leftover = Number(total - distributed);
  for (let i = 0; i < leftover; i += 1) {
    const target = order[i];
    if (!target) break;
    shares[target.index] = (shares[target.index] ?? 0n) + 1n;
  }

  return shares.map((amount) => ({
    amount: negative ? -amount : amount,
    currency: m.currency,
  }));
}

/**
 * Cash rounding: snaps an amount to a multiple of `increment` minor units —
 * Cambodia rounds cash to 100 KHR.
 *
 * `03-data-model.md` § 5.3 is explicit that this applies to the amount tendered
 * and never to the bill total, so that the invoice and its tax breakdown still
 * reconcile afterwards. Nothing here writes back to an order; the caller
 * records the rounding as its own line.
 */
export function cashRound(
  m: Money,
  increment: bigint,
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money {
  if (increment <= 0n) throw new RangeError("cashRound: increment must be positive");
  if (increment === 1n) return m;
  return {
    amount: divideRounded(m.amount, increment, mode) * increment,
    currency: m.currency,
  };
}

/** Parses "12.34" as USD into 1234 minor units, using the currency's exponent. */
export function parseMoney(decimal: string, code: CurrencyCode): Money {
  const exponent = minorUnitOf(code);
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(decimal.trim());
  if (!match) throw new SyntaxError(`parseMoney: not a decimal number: ${decimal}`);
  const [, sign, whole, fraction = ""] = match;
  if (fraction.length > exponent) {
    throw new RangeError(
      `parseMoney: ${decimal} has more precision than ${code} allows (${exponent} decimals)`,
    );
  }
  const padded = fraction.padEnd(exponent, "0");
  const amount = BigInt(`${whole}${padded}`) * (sign === "-" ? -1n : 1n);
  return { amount, currency: code };
}

/**
 * Renders minor units as a plain decimal string — no symbol, no grouping. The
 * `minorUnit` override exists because settled orders snapshot their exponent
 * and must render from the snapshot, not from today's registry.
 */
export function toDecimalString(m: Money, minorUnit = minorUnitOf(m.currency)): string {
  const divisor = pow10(minorUnit);
  const negative = m.amount < 0n;
  const abs = negative ? -m.amount : m.amount;
  const whole = abs / divisor;
  const sign = negative ? "-" : "";
  if (minorUnit === 0) return `${sign}${whole}`;
  const fraction = (abs % divisor).toString().padStart(minorUnit, "0");
  return `${sign}${whole}.${fraction}`;
}
